import { useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { api } from '../../services/api'
import { isLegacyOrigin } from '../../utils/canonicalOrigin'

const SYNCED_KEY = 'bt_push_synced'

/**
 * Re-POSTs this device's existing push subscription once per session.
 *
 * Two things drift without it. A shared phone that signs into a second account
 * leaves the row pointing at the first one — the endpoint is the identity, so
 * re-posting reassigns it. And a browser that silently rotates its endpoint
 * would otherwise need a `pushsubscriptionchange` handler, which cannot
 * authenticate: the service worker has no access to the Bearer token in
 * localStorage. Endpoints this sync can no longer see are pruned server-side by
 * the next 410.
 *
 * Renders nothing; sits beside PwaTracker in GlobalOverlays.
 */
export function PushSubscriptionSync() {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) return
    if (isLegacyOrigin()) return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    if (sessionStorage.getItem(SYNCED_KEY)) return

    let cancelled = false

    // getRegistration() first: `ready` never settles when no service worker is
    // registered, which is the normal state under `vite dev`.
    navigator.serviceWorker
      .getRegistration()
      .then((registration) => registration?.pushManager.getSubscription() ?? null)
      .then(async (subscription) => {
        if (cancelled || !subscription) return
        const response = await api.vision.push.subscribe({
          subscription: subscription.toJSON(),
          userAgent: navigator.userAgent,
          appHost: window.location.hostname,
        })
        // Only mark the session done on success, so a backend blip retries on
        // the next load rather than skipping the whole session.
        if (response.success) sessionStorage.setItem(SYNCED_KEY, '1')
      })
      .catch(() => {
        // Non-critical — the next load tries again.
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  return null
}
